import {
  ExecutionStatus,
  getBulkActionJobData,
  type JobCounterKey,
  type JobExecutionStatus,
} from '@cdw-ae/common-data';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import type { TrackedWorkflowName } from './jobs-status.models';
import {
  type HydratedLock,
  JOBS_STATUS_FEATURE_KEY,
  type JobsStatusState,
} from './jobs-status.reducer';

// Feature selector
export const selectJobsStatusState = createFeatureSelector<JobsStatusState>(
  JOBS_STATUS_FEATURE_KEY
);

// Basic state selectors
export const selectActiveExecutions = createSelector(
  selectJobsStatusState,
  (state) => state.activeExecutions
);

export const selectFinishedExecutions = createSelector(
  selectJobsStatusState,
  (state) => state.finishedExecutions
);

export const selectJobsStatusBound = createSelector(
  selectJobsStatusState,
  (state) => state.bound
);

export const selectJobsStatusLoaded = createSelector(
  selectJobsStatusState,
  (state) => state.loaded
);

export const selectJobsStatusError = createSelector(
  selectJobsStatusState,
  (state) => state.error
);

// Derived selectors for specific workflows
export const selectActiveExecutionsByWorkflow = createSelector(
  selectActiveExecutions,
  (executions): Record<TrackedWorkflowName, JobExecutionStatus | null> => {
    const grouped: Record<TrackedWorkflowName, JobExecutionStatus | null> = {
      'full-asset-sync-workflow': null,
    };

    for (const execution of executions) {
      const workflowName = execution.workflowName as TrackedWorkflowName;
      if (workflowName in grouped) {
        grouped[workflowName] = execution;
      }
    }

    return grouped;
  }
);

// Status indicator selector
export const selectIsFullAssetSyncRunning = createSelector(
  selectActiveExecutionsByWorkflow,
  (grouped) => grouped['full-asset-sync-workflow'] !== null
);

// Combined selector for any cache refresh running
export const selectIsAnyCacheRefreshRunning = createSelector(
  selectActiveExecutions,
  (executions) => executions.length > 0
);

// Finished executions grouped by workflow
export const selectFinishedExecutionsByWorkflow = createSelector(
  selectFinishedExecutions,
  (executions): Record<TrackedWorkflowName, JobExecutionStatus | null> => {
    const byWorkflow: Record<TrackedWorkflowName, JobExecutionStatus | null> = {
      'full-asset-sync-workflow': null,
    };

    for (const execution of executions) {
      const workflowName = execution.workflowName as TrackedWorkflowName;
      if (workflowName in byWorkflow) {
        byWorkflow[workflowName] = execution;
      }
    }

    return byWorkflow;
  }
);

/**
 * Last updated timestamp from the most recent full-asset-sync-workflow execution
 * Returns the completion time (dt) from SQL-persisted job data
 * Returns null if no completed cache refresh exists
 */
export const selectCacheLastUpdated = createSelector(
  selectJobsStatusState,
  (state): Date | null => {
    const workflows = state.workflowExecutions['full-asset-sync-workflow'];
    if (!workflows || workflows.length === 0) {
      return null;
    }
    return workflows[0].dt ?? null;
  }
);

// Active full-asset-sync execution (convenience selector for reading counters)
const selectActiveFullAssetSync = createSelector(
  selectActiveExecutionsByWorkflow,
  (grouped) => grouped['full-asset-sync-workflow']
);

// Factory for asset count selectors from the full-asset-sync-workflow execution's jobCounters
function createAssetCountSelector(key: JobCounterKey) {
  return createSelector(
    selectActiveFullAssetSync,
    (execution): number => {
      const value = execution?.jobCounters?.[key];
      return typeof value === 'number' ? value : 0;
    }
  );
}

export const selectDeviceCacheAssetCount = createAssetCountSelector(
  'totalDeviceAssetsWritten'
);
export const selectUserCacheAssetCount = createAssetCountSelector(
  'totalUsersAssetsWritten'
);
export const selectGroupingsAssetCount = createAssetCountSelector(
  'totalGroupingsAssetsWritten'
);

// Formatted count helper - shared logic for "k" suffix formatting
const formatCount = (count: number): string | null => {
  if (!count) return null;
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return count.toString();
};

export const selectFormattedDeviceCount = createSelector(
  selectDeviceCacheAssetCount,
  (count): string | null => formatCount(count)
);

export const selectFormattedUserCount = createSelector(
  selectUserCacheAssetCount,
  (count): string | null => formatCount(count)
);

export const selectFormattedGroupingsCount = createSelector(
  selectGroupingsAssetCount,
  (count): string | null => formatCount(count)
);

// Tab-based job selectors
export const selectMyJobs = createSelector(
  selectJobsStatusState,
  (state) => state.myJobs
);

export const selectAllJobs = createSelector(
  selectJobsStatusState,
  (state) => state.allJobs
);

export const selectActiveTab = createSelector(
  selectJobsStatusState,
  (state) => state.activeTab
);

// Count of all active jobs (running or pending)
export const selectActiveJobsCount = createSelector(selectMyJobs, (myJobs) => {
  return myJobs.filter(
    (execution) =>
      execution.executionStatus === ExecutionStatus.started ||
      execution.executionStatus === ExecutionStatus.pending
  ).length;
});

/**
 * Factory function to select a specific job execution by jobId
 * Searches in-progress jobs (Firestore) first, then completed jobs (SQL)
 * Used by components that need to monitor a specific job through its full lifecycle
 *
 * Under the two-tier storage model, jobs are deleted from Firestore on completion
 * and persisted to SQL. This selector bridges both tiers so components can track
 * a job from creation through completion without losing visibility during the transition.
 */
export const selectJobExecutionByJobId = (jobId: number) =>
  createSelector(
    selectMyJobs,
    selectAllJobs,
    selectMyCompletedJobs,
    selectAllCompletedJobs,
    (myJobs, allJobs, myCompleted, allCompleted): JobExecutionStatus | null => {
      // Search in-progress jobs first (Firestore-sourced, real-time)
      const myJob = myJobs.find((job) => job.jobId === jobId);
      if (myJob) return myJob;

      const allJob = allJobs.find((job) => job.jobId === jobId);
      if (allJob) return allJob;

      // Fallback to completed jobs (SQL-sourced, fetched on job disappearance)
      const myCompletedJob = myCompleted.find((job) => job.jobId === jobId);
      if (myCompletedJob) return myCompletedJob;

      const allCompletedJob = allCompleted.find((job) => job.jobId === jobId);
      return allCompletedJob ?? null;
    }
  );

/**
 * Factory function to select a specific job execution by correlationId
 * Searches in-progress jobs (Firestore) first, then completed jobs (SQL)
 * Used by components that track jobs via client-generated correlationId
 */
export const selectJobExecutionByCorrelationId = (correlationId: string) =>
  createSelector(
    selectMyJobs,
    selectAllJobs,
    selectMyCompletedJobs,
    selectAllCompletedJobs,
    (myJobs, allJobs, myCompleted, allCompleted): JobExecutionStatus | null => {
      // Search in-progress jobs first (Firestore-sourced, real-time)
      const myJob = myJobs.find((job) => job.correlationId === correlationId);
      if (myJob) return myJob;

      const allJob = allJobs.find((job) => job.correlationId === correlationId);
      if (allJob) return allJob;

      // Fallback to completed jobs (SQL-sourced, fetched on job disappearance)
      const myCompletedJob = myCompleted.find(
        (job) => job.correlationId === correlationId
      );
      if (myCompletedJob) return myCompletedJob;

      const allCompletedJob = allCompleted.find(
        (job) => job.correlationId === correlationId
      );
      return allCompletedJob ?? null;
    }
  );

// Completed jobs selectors (from SQL with Redis caching)
export const selectMyCompletedJobs = createSelector(
  selectJobsStatusState,
  (state) => state.myCompletedJobs
);

export const selectAllCompletedJobs = createSelector(
  selectJobsStatusState,
  (state) => state.allCompletedJobs
);

export const selectMyCompletedJobsLoading = createSelector(
  selectJobsStatusState,
  (state) => state.myCompletedJobsLoading
);

export const selectAllCompletedJobsLoading = createSelector(
  selectJobsStatusState,
  (state) => state.allCompletedJobsLoading
);

export const selectMyCompletedJobsError = createSelector(
  selectJobsStatusState,
  (state) => state.myCompletedJobsError
);

export const selectAllCompletedJobsError = createSelector(
  selectJobsStatusState,
  (state) => state.allCompletedJobsError
);

/**
 * Status priority for sorting jobs (lower number = higher priority)
 */
const JOB_STATUS_PRIORITY: Record<ExecutionStatus, number> = {
  [ExecutionStatus.started]: 0,
  [ExecutionStatus.pending]: 1,
  [ExecutionStatus.finished]: 2,
  [ExecutionStatus.failed]: 3,
  [ExecutionStatus.error]: 3,
  [ExecutionStatus.cancelledRunning]: 4,
  [ExecutionStatus.cancelledPending]: 4,
};

/**
 * Comparator function for sorting jobs by status priority, then by time
 */
function compareJobsByPriorityAndTime(
  a: JobExecutionStatus,
  b: JobExecutionStatus
): number {
  // First, compare by status priority
  const statusDiff =
    JOB_STATUS_PRIORITY[a.executionStatus] -
    JOB_STATUS_PRIORITY[b.executionStatus];
  if (statusDiff !== 0) return statusDiff;

  // Same status, sort by time (most recent first)
  const timeA = a.createdAt?.getTime() ?? a.dt?.getTime() ?? 0;
  const timeB = b.createdAt?.getTime() ?? b.dt?.getTime() ?? 0;
  return timeB - timeA;
}

/**
 * Merge in-progress jobs (from Firestore) with completed jobs (from SQL)
 * for the "My Jobs" tab
 * Combines both sources and limits to 15 jobs total
 * Sorted by status priority (started → pending → finished → failed), then by time
 */
export const selectMyJobsMerged = createSelector(
  selectMyJobs,
  selectMyCompletedJobs,
  (inProgress, completed): JobExecutionStatus[] => {
    const allJobs = [...inProgress, ...completed];
    const sorted = allJobs.sort(compareJobsByPriorityAndTime);
    return sorted.slice(0, 15);
  }
);

/**
 * Merge in-progress jobs (from Firestore) with completed jobs (from SQL)
 * for the "All Jobs" tab
 * Combines both sources and limits to 15 jobs total
 * Sorted by status priority (started → pending → finished → failed), then by time
 */
export const selectAllJobsMerged = createSelector(
  selectAllJobs,
  selectAllCompletedJobs,
  (inProgress, completed): JobExecutionStatus[] => {
    const allJobs = [...inProgress, ...completed];
    const sorted = allJobs.sort(compareJobsByPriorityAndTime);
    return sorted.slice(0, 15);
  }
);

// ---------------------------------------------------------------------------
// SSRM grid selection-lock failsafe selectors (fix/SSRM-row-activity-ui)
// ---------------------------------------------------------------------------

export const selectHydratedLocks = createSelector(
  selectJobsStatusState,
  (state) => state.hydratedLocks
);

export const selectReleasedLockSessions = createSelector(
  selectJobsStatusState,
  (state) => state.releasedLockSessions
);

/**
 * Pure lookup over a `hydratedLocks` map: find the first entry whose `jobId`
 * matches. Shared by both `selectHydratedLockByJobId` (for store consumers)
 * and the effect-side `resolveSessionFromCompletion` helper, so the lookup
 * shape is identical regardless of caller.
 *
 * First-match-wins is safe because saas-jobs-api mints globally-unique jobIds
 * and `hydratedLocks` only contains jobs THIS tab enqueued (single-writer
 * contract). If a future migration replaces the Record with a Map, callers
 * pinning iteration-order will flag the change in their specs.
 */
export function findHydratedLockByJobId(
  hydrated: Record<string, HydratedLock>,
  jobId: number
): [string, HydratedLock] | undefined {
  for (const entry of Object.entries(hydrated)) {
    if (entry[1].jobId === jobId) return entry;
  }
  return undefined;
}

/**
 * Factory: find the first hydrated lock whose `jobId` matches the given id.
 * Returns the `[gridSessionUuid, HydratedLock]` tuple, or `undefined` if no
 * lock owns that jobId. Memoizes per (jobId, hydrated-identity) pair.
 */
export const selectHydratedLockByJobId = (jobId: number) =>
  createSelector(selectHydratedLocks, (hydrated) =>
    findHydratedLockByJobId(hydrated, jobId)
  );

export const selectNowMs = createSelector(
  selectJobsStatusState,
  (state) => state.nowMs
);

/**
 * Threshold formula for declaring a bulk job "stuck":
 *   threshold_ms = 10 minutes + 1 minute per 5,000 selected assets
 *                = 600_000 + (selectedCount / 5_000) * 60_000
 *
 * Exported so the consumer (effect scheduling per-job timers) can compute the
 * same delay used by the selector — single source of truth, no drift between
 * "when does the timer fire?" and "when does the banner appear?".
 */
export function computeStuckThresholdMs(selectedCount: number): number {
  const safeCount = Number.isFinite(selectedCount) && selectedCount > 0
    ? selectedCount
    : 0;
  return 600_000 + (safeCount / 5_000) * 60_000;
}

/**
 * Factory: emits the user's in-flight bulk-action jobs scoped to a specific
 * gridSessionUuid (one per browser tab; minted in sessionStorage by
 * CdwSsrmGridComponent). Filters out terminal-state and other-session jobs.
 *
 * The factory pattern (function returning a selector) is required because the
 * gridSessionUuid is consumer-supplied and can vary per component instance.
 * Memoization is per-instance — callers should cache the returned selector
 * (e.g., in a class field) rather than calling the factory in a template.
 */
export const selectActiveBulkJobsForGridSession = (gridSessionUuid: string) =>
  createSelector(selectMyJobs, (myJobs): JobExecutionStatus[] => {
    return myJobs.filter((job) => {
      if (
        job.executionStatus !== ExecutionStatus.pending &&
        job.executionStatus !== ExecutionStatus.started
      ) {
        return false;
      }
      const data = getBulkActionJobData(job.additionalJobData);
      return data.gridSessionUuid === gridSessionUuid;
    });
  });

/**
 * Factory: true when the selection lock should be enforced for this grid
 * session. Drives `selectionLockActive$` on the SSRM grid component.
 *
 * Three sources combine, with the optimistic-release marker as the trump card:
 *   1. If session is in `releasedLockSessions` → false (recently released
 *      locally; ignore Firestore until terminal-state delete propagates)
 *   2. Otherwise, if a hydrated entry exists for this session → true
 *      (pessimistic — assume locked until Firestore confirms otherwise)
 *   3. Otherwise, if any active bulk job is scoped to this session → true
 */
export const selectIsLockActiveForGridSession = (gridSessionUuid: string) =>
  createSelector(
    selectHydratedLocks,
    selectReleasedLockSessions,
    selectActiveBulkJobsForGridSession(gridSessionUuid),
    (hydrated, released, activeJobs): boolean => {
      if (released.includes(gridSessionUuid)) return false;
      if (hydrated[gridSessionUuid]) return true;
      return activeJobs.length > 0;
    }
  );

/**
 * Factory: emits active jobs for this session that have exceeded the threshold
 * formula since their last status update. Drives the stuck-job banner.
 *
 * "Last update" comes from the server-injected `dt` field (serverTimestamp on
 * every Firestore write via IStatusWriter Firebase strategy). The dt value
 * advances on every per-step status update emitted by the workflow YAML,
 * making it an effective heartbeat without needing a dedicated heartbeat
 * field — see the workflow callbacks at apps/saas-jobs/workflows/*.yaml.
 *
 * Defensive fallbacks:
 *   - dt missing → use createdAt (only happens for malformed records)
 *   - both missing → never stuck (no signal to evaluate)
 *   - selectedCount missing → use 0, formula degenerates to flat 10 minutes
 *   - nowMs = 0 (clock not yet ticked by nowTick$ effect) → empty result
 *     prevents false-positive stuck detection during initial render
 */
export const selectStuckJobsForGridSession = (gridSessionUuid: string) =>
  createSelector(
    selectActiveBulkJobsForGridSession(gridSessionUuid),
    selectNowMs,
    (activeJobs, nowMs): JobExecutionStatus[] => {
      if (nowMs === 0) return [];
      return activeJobs.filter((job) => {
        const lastTouchMs =
          job.dt?.getTime?.() ?? job.createdAt?.getTime?.() ?? null;
        if (lastTouchMs === null) return false;
        const data = getBulkActionJobData(job.additionalJobData);
        const thresholdMs = computeStuckThresholdMs(data.selectedCount ?? 0);
        return nowMs - lastTouchMs > thresholdMs;
      });
    }
  );
